import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Loader2, Send, Calendar,
  MapPin, ArrowLeft, ExternalLink, Trash2,
} from "lucide-react";
import { api } from "../lib/api";
import { useUser } from "../contexts/UserContext";
import { useAuthors } from "../hooks/useAuthors";
import CategoryBadge from "../components/CategoryBadge";
import PhotoCarousel from "../components/PhotoCarousel";
import ReactionSummary from "../components/ReactionSummary";
import RichText from "../components/RichText";
import LinkedInPublishButton from "../components/LinkedInPublishButton";
// Les commentaires restent en texte simple (pas d'éditeur markdown côté
// commentaire) : Linkify suffit et évite d'y interpréter des marqueurs.
import { Linkify } from "../components/Linkify";
import VideoEmbed from "../components/VideoEmbed";
import { REACTIONS, emptySummary } from "../lib/reactions";
import { findVideo } from "../lib/media";
import { formatDateTime, timeAgo } from "../lib/date";
import { Skeleton } from "../components/Skeleton";

const RSVP_OPTIONS = [
  ["attending", "Oui"],
  ["maybe", "Peut-être"],
  ["declined", "Non"],
];

export default function PostDetail() {
  const { id } = useParams();
  const { user } = useUser();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [category, setCategory] = useState(null);
  const [myReactions, setMyReactions] = useState([]);
  const [reactionSummary, setReactionSummary] = useState(emptySummary());
  const [linkedinStatus, setLinkedinStatus] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [attending, setAttending] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: postData } = await api.get(`/posts/${id}`);
      setPost(postData);
      setAttending(postData.my_attending_status || null);
      const [catsRes, mrRes, cmRes, rsRes, liRes] = await Promise.all([
        api.get("/categories"),
        api.get(`/posts/${id}/reactions/mine`),
        api.get(`/posts/${id}/comments`),
        // Détail par type. Un échec ici ne doit pas empêcher l'affichage du post.
        api.get(`/posts/${id}/reactions/summary`).catch(() => ({ data: emptySummary() })),
        // Statut LinkedIn : brique optionnelle, son absence n'empêche rien.
        api.get("/linkedin/status").catch(() => ({ data: null })),
      ]);
      setCategory(catsRes.data.find((c) => c.category_id === postData.category_id));
      setMyReactions(mrRes.data);
      setComments(cmRes.data);
      setReactionSummary(rsRes.data || emptySummary());
      setLinkedinStatus(liRes.data);
    } catch (e) {
      // 404 → contenu supprimé ou inaccessible : toast UX + retour feed
      if (e?.response?.status === 404) {
        toast.error("Ce contenu n'est plus disponible.");
        navigate("/", { replace: true });
        return;
      }
      setError(e?.response?.data?.detail || "Post introuvable.");
    } finally {
      setLoading(false);
    }
    // Stable hors `id` : `api`/`toast`/`navigate` sont stables, les setters useState aussi.
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  // Résolution auteur du post + auteurs des commentaires (back-end les renvoie déjà
  // mais on hydrate aussi l'auteur du post via le hook batch)
  const authorKeys = useMemo(() => (post ? [post.author_id] : []), [post]);
  const authors = useAuthors(authorKeys);
  const author = post ? authors[post.author_id] : null;

  // Vidéo YouTube / Vimeo citée dans le contenu du post.
  const video = useMemo(() => findVideo(post?.content), [post?.content]);

  const toggle = async (type) => {
    try {
      const { data } = await api.post(`/posts/${id}/reactions`, { reaction_type: type });
      const added = data.action === "added";
      setMyReactions((prev) =>
        added ? [...prev, type] : prev.filter((t) => t !== type)
      );
      setPost((p) => ({ ...p, reactions_count: data.reactions_count }));
      // Mise à jour optimiste du détail par type : le compteur bouge tout de
      // suite, sans second aller-retour réseau.
      setReactionSummary((s) => {
        const base = s || emptySummary();
        const next = { ...base.counts, [type]: Math.max(0, (base.counts[type] || 0) + (added ? 1 : -1)) };
        return { counts: next, total: Object.values(next).reduce((a, b) => a + b, 0) };
      });
    } catch {
      toast.error("Échec de la réaction.");
    }
  };

  const postComment = async () => {
    if (newComment.trim().length < 2) return;
    setSubmitting(true);
    try {
      await api.post(`/posts/${id}/comments`, {
        content: newComment,
        original_language: user?.language_preference || "fr",
      });
      toast.success("Commentaire envoyé en modération.");
      setNewComment("");
      // Reload pour afficher le commentaire pending de l'auteur
      const { data: refreshed } = await api.get(`/posts/${id}/comments`);
      setComments(refreshed);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec.");
    } finally {
      setSubmitting(false);
    }
  };

  const deletePost = async () => {
    if (!window.confirm("Supprimer ce post ? Action irréversible.")) return;
    try {
      await api.delete(`/posts/${id}`);
      toast.success("Post supprimé.");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec suppression.");
    }
  };

  const setRsvp = async (status) => {
    try {
      const { data } = await api.post(`/events/${id}/attendees`, { status });
      setAttending(data.my_status);
      setPost((p) => ({ ...p, attendees_count: data.attendees_count }));
      toast.success("Inscription mise à jour.");
    } catch {
      toast.error("Échec inscription.");
    }
  };

  if (loading) {
    return (
      <div data-testid="post-detail-loading" className="max-w-2xl mx-auto">
        <Skeleton className="w-32 h-4 mb-4" />
        <div
          className="bg-white rounded-[12px] p-6 sm:p-8 mb-4"
          style={{ border: "1px solid #E5E5E5" }}
        >
          <Skeleton className="w-24 h-6 mb-4" />
          <Skeleton className="w-3/4 h-8 mb-3" />
          <Skeleton className="w-full h-4 mb-2" />
          <Skeleton className="w-5/6 h-4 mb-2" />
          <Skeleton className="w-2/3 h-4" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="post-detail-error" className="max-w-2xl mx-auto text-center py-20">
        <p className="text-lg mb-3" style={{ color: "#EF4444" }}>
          {error}
        </p>
        <button
          onClick={() => navigate("/")}
          className="text-sm font-medium underline"
          style={{ color: "#F77F00" }}
        >
          Retour au feed
        </button>
      </div>
    );
  }

  const isEvent = !!post.event_date;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid="post-detail-page"
      className="max-w-2xl mx-auto"
    >
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm mb-4 transition-colors hover:text-black"
        style={{ color: "#666" }}
        data-testid="post-back-link"
      >
        <ArrowLeft size={14} /> Retour au feed
      </Link>

      <article
        className="bg-white rounded-[12px] p-6 sm:p-8 mb-4"
        style={{ border: "1px solid #E5E5E5" }}
      >
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <CategoryBadge category={category} />
          <div className="flex items-center gap-2">
            {post.target_scope === "global" && (
              <span
                className="text-[11px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full"
                style={{ background: "#FFF7ED", color: "#9A3412" }}
              >
                Visible monde entier
              </span>
            )}
            {(post.author_id === user?.user_key ||
              ["ceo", "direction"].includes(user?.hierarchy_level)) && (
              <button
                onClick={deletePost}
                data-testid="post-detail-delete"
                className="p-1.5 rounded-full hover:bg-red-50 transition-colors border"
                style={{ borderColor: "#FECACA" }}
                title="Supprimer ce post"
              >
                <Trash2 size={14} color="#EF4444" />
              </button>
            )}
          </div>
        </div>

        <h1
          className="text-2xl sm:text-3xl font-bold mb-4 leading-tight tracking-tight"
          style={{ fontFamily: "'Sora', sans-serif" }}
          data-testid="post-title"
        >
          {post.title}
        </h1>

        {/* Author bar */}
        <div className="flex items-center gap-3 mb-6 pb-4 border-b" style={{ borderColor: "#E5E5E5" }}>
          {author?.avatar_url ? (
            <img src={author.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white"
              style={{ background: "#F77F00" }}
            >
              {(author?.first_name?.[0] || "?") + (author?.last_name?.[0] || "")}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-sm font-semibold" data-testid="post-author-name">
              {author?.name || "Membre APOLLO"}
            </div>
            <div className="text-xs" style={{ color: "#666" }}>
              {author?.hierarchy_level || ""}
              {author?.hierarchy_level && " · "}
              Publié {timeAgo(post.published_at || post.created_at)}
            </div>
          </div>
        </div>

        {post.photos && post.photos.length > 0 && (
          <div className="mb-6">
            <PhotoCarousel
              photos={post.photos}
              testid="post-detail-photos"
            />
          </div>
        )}

        <div className="text-base leading-relaxed mb-6" data-testid="post-content">
          <RichText>{post.content}</RichText>
        </div>

        {video && (
          <div className="mb-6">
            <VideoEmbed video={video} testid="post-detail-video" />
          </div>
        )}

        {post.external_url && (
          <a
            href={post.external_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-semibold mb-6"
            style={{ color: "#F77F00" }}
            data-testid="post-external-url"
          >
            <ExternalLink size={14} /> Lien externe
          </a>
        )}

        {isEvent && (
          <div
            className="flex items-start gap-3 p-4 rounded-lg mb-6"
            style={{ background: "#FFF7ED", border: "1px solid #FED7AA" }}
            data-testid="post-event-info"
          >
            <Calendar size={20} color="#F77F00" className="flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold" style={{ color: "#9A3412" }}>
                {formatDateTime(post.event_date)}
              </div>
              {post.event_location && (
                <div className="text-sm flex items-center gap-1 mt-1" style={{ color: "#9A3412" }}>
                  <MapPin size={12} />
                  {post.event_location}
                </div>
              )}
              {post.event_registration_url && (
                <a
                  href={post.event_registration_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-semibold underline mt-2 inline-block"
                  style={{ color: "#F77F00" }}
                  data-testid="post-event-registration-url"
                >
                  S'inscrire en ligne →
                </a>
              )}
            </div>
          </div>
        )}

        {isEvent && (
          <div className="mb-6">
            <div className="text-sm font-semibold mb-2">Je participe ?</div>
            <div className="flex gap-2">
              {RSVP_OPTIONS.map(([k, l]) => {
                const active = attending === k;
                return (
                  <button
                    key={k}
                    onClick={() => setRsvp(k)}
                    data-testid={`rsvp-${k}`}
                    className="flex-1 py-2.5 rounded-lg text-sm font-semibold border transition-all active:scale-95"
                    style={{
                      background: active ? "#F77F00" : "#FFF",
                      color: active ? "#FFF" : "#1A1A1A",
                      borderColor: active ? "#F77F00" : "#E5E5E5",
                    }}
                  >
                    {l}
                  </button>
                );
              })}
            </div>
            <div className="text-xs mt-2" style={{ color: "#666" }} data-testid="rsvp-count">
              {post.attendees_count || 0} participant
              {(post.attendees_count || 0) > 1 ? "s" : ""} confirmé
              {(post.attendees_count || 0) > 1 ? "s" : ""}
            </div>
          </div>
        )}

        {/* Compteurs — combien de J'aime / Bravo / Idée / Coup de cœur,
            et combien de commentaires. Masqué tant que tout est à zéro. */}
        <div className="pt-4 border-t" style={{ borderColor: "#E5E5E5" }}>
          <ReactionSummary
            summary={reactionSummary}
            commentsCount={comments.length}
            detailed
            className="mb-3"
            testid="post-detail-reaction-summary"
          />
        </div>

        {/* Reactions */}
        <div
          className="flex items-center gap-2 flex-wrap"
        >
          {REACTIONS.map((r) => {
            const active = myReactions.includes(r.type);
            return (
              <button
                key={r.type}
                onClick={() => toggle(r.type)}
                data-testid={`reaction-${r.type}`}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all active:scale-95"
                style={{
                  background: active ? "#FFF7ED" : "#FFF",
                  borderColor: active ? "#F77F00" : "#E5E5E5",
                  color: active ? "#F77F00" : "#1A1A1A",
                }}
              >
                <r.Icon size={14} /> {r.label}
                {(reactionSummary?.counts?.[r.type] || 0) > 0 && (
                  <span
                    className="ml-0.5 tabular-nums"
                    style={{ color: active ? "#F77F00" : "#666" }}
                    data-testid={`reaction-count-${r.type}`}
                  >
                    {reactionSummary.counts[r.type]}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Le bouton « Partager sur LinkedIn » (partage d'un LIEN) a été retiré
            le 20/08/2026. Break News est un réseau interne : partager son URL
            produisait sur LinkedIn une carte pointant vers une page que
            personne d'extérieur ne peut ouvrir, et exposait l'adresse d'un
            contenu interne. La publication ci-dessous reproduit le post
            lui-même — titre, texte et image — sans aucun lien vers l'app. */}

        {/* Publication directe sur le profil LinkedIn de l'utilisateur.
            Le composant se masque tout seul si le post n'est pas publié ou
            si l'endpoint de statut est indisponible. */}
        <LinkedInPublishButton
          post={post}
          status={linkedinStatus}
          onPublished={(r) =>
            setPost((p) => ({
              ...p,
              linkedin_post_urn: r.post_urn,
              linkedin_post_url: r.post_url,
            }))
          }
        />
      </article>

      {/* Comments */}
      <div
        className="bg-white rounded-[12px] p-6 sm:p-8"
        style={{ border: "1px solid #E5E5E5" }}
        data-testid="comments-section"
      >
        <h2 className="text-lg font-bold mb-4" style={{ fontFamily: "'Sora', sans-serif" }}>
          {category?.encourages_comments
            ? "Réponses & idées de la communauté"
            : `Commentaires (${comments.length})`}
        </h2>
        {comments.length === 0 && (
          <p className="text-sm py-4" style={{ color: "#666" }}>
            Aucun commentaire pour l'instant. Soyez le premier !
          </p>
        )}
        {comments.map((c) => (
          <div
            key={c.comment_id}
            className="py-3 border-b last:border-b-0"
            style={{ borderColor: "#E5E5E5" }}
            data-testid={`comment-${c.comment_id}`}
          >
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-sm font-semibold">{c.author?.name || "Membre"}</span>
              <span className="text-xs" style={{ color: "#666" }}>
                {timeAgo(c.created_at)}
              </span>
              {c.status === "pending" && (
                <span
                  className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                  style={{ background: "#FFF7ED", color: "#9A3412" }}
                  data-testid={`comment-pending-${c.comment_id}`}
                >
                  En attente de modération
                </span>
              )}
            </div>
            <p className="text-sm"><Linkify>{c.content}</Linkify></p>
          </div>
        ))}

        <div className="mt-4 pt-4 border-t" style={{ borderColor: "#E5E5E5" }}>
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Ajouter un commentaire…"
            rows={2}
            maxLength={500}
            data-testid="comment-input"
            className="w-full p-3 rounded-lg border text-sm outline-none focus:border-orange-500 transition-colors"
            style={{ borderColor: "#E5E5E5" }}
          />
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs" style={{ color: "#666" }}>
              Modération par votre DR avant publication.
            </p>
            <button
              onClick={postComment}
              disabled={submitting || newComment.trim().length < 2}
              data-testid="comment-submit"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-white disabled:opacity-50 transition-opacity hover:opacity-90"
              style={{ background: "#F77F00" }}
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              <Send size={14} /> Envoyer
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
